import { DeesElement, property, html, customElement, type TemplateResult, directives } from '@design.estate/dees-element';
import {
  TextLayer,
  type PDFDocumentProxy,
  type PDFPageProxy,
  type RenderTask,
} from 'pdfjs-dist';
import { PdfManager } from '../dees-pdf-shared/PdfManager.js';
import {
  raceWithAbort,
  settlePromisesWithin,
  waitForAnimationFrame,
} from '../dees-pdf-shared/utils.js';
import { viewerStyles } from './styles.js';
import { demo as demoFunc } from './demo.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-layout/dees-tile/dees-tile.js';
import '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';
import { mediaControls } from '../viewer.styles.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-pdf-viewer': DeesPdfViewer;
  }
}

type RenderState = 'idle' | 'loading' | 'rendering-main' | 'rendering-thumbs' | 'rendered' | 'error' | 'disposed';

interface ILoadOperation {
  key: string;
  promise: Promise<void>;
}

interface IRenderRecoveryState {
  currentPage: number;
  currentZoom: number;
  viewportMode: 'auto' | 'page-fit' | 'page-width' | 'custom';
}

const pipelineSettlementGraceMs = 2_500;

interface ITextLayerRenderTask {
  textLayer: TextLayer;
  promise: Promise<unknown>;
}

@customElement('dees-pdf-viewer')
export class DeesPdfViewer extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Media', 'PDF'];
  public static styles = [...viewerStyles, mediaControls];

  @property({ type: String }) accessor viewMode: 'document' | 'gallery' = 'document';
  @property({ type: Boolean, reflect: true }) accessor frameless = false;
  @property({ type: Boolean }) accessor showFooter = true;
  @property({ type: String }) accessor filename = '';
  @property({ type: String }) accessor loadError = '';

  private get galleryActive() { return this.viewMode === 'gallery' && !this.loading && !this.loadError; }

  private get thumbnailsVisible() { return this.showSidebar || this.viewMode === 'gallery'; }

  @property({ type: String })
  accessor pdfUrl: string = '';

  @property({ type: Number })
  accessor initialPage: number = 1;

  @property({ type: String })
  accessor initialZoom: 'auto' | 'page-fit' | 'page-width' | number = 'auto';

  @property({ type: Boolean })
  accessor showToolbar: boolean = true;

  @property({ type: Boolean })
  accessor showSidebar: boolean = false;

  @property({ type: String })
  accessor sidebarPosition: 'left' | 'right' = 'left';

  @property({ type: Number })
  accessor currentPage: number = 1;

  @property({ type: Number })
  accessor totalPages: number = 1;

  @property({ type: Number })
  accessor currentZoom: number = 1;

  @property({ type: Boolean })
  accessor loading: boolean = false;

  @property({ type: String })
  accessor documentId: string = '';

  @property({ type: Array })
  accessor thumbnailData: Array<{page: number, rendered: boolean, width?: number}> = [];

  @property({ type: Array })
  accessor pageData: Array<{page: number, rendered: boolean, rendering: boolean, textLayerRendered: boolean}> = [];

  @property({ type: Number })
  accessor pdfFileSize: number = 0;

  private pdfDocument: PDFDocumentProxy | null = null;
  private pdfLoadPromise: Promise<PDFDocumentProxy> | null = null;
  private loadOperation: ILoadOperation | null = null;
  private loadGeneration = 0;
  private connectionEpoch = 0;
  private cleanupPromise: Promise<void> | null = null;
  private renderAdmissionOpen = false;
  private renderGeneration = 0;
  private renderState: RenderState = 'idle';
  private renderAbortController: AbortController | null = null;
  private pageRendering: boolean = false;
  private pageNumPending: number | null = null;
  private currentRenderTask: any = null;
  private currentRenderPromise: Promise<void> | null = null;
  private thumbnailRenderTasks = new Set<RenderTask>();
  private thumbnailRenderPromise: Promise<void> | null = null;
  private thumbnailRenderGeneration = 0;
  private thumbnailRenderQueued = false;
  private thumbnailRenderQueueEpoch = 0;
  private pageRenderTasks = new Map<number, RenderTask>();
  private pageRenderPipelines = new Set<Promise<void>>();
  private documentUsePipelines = new Set<Promise<void>>();
  private rerenderPromise: Promise<void> | null = null;
  private rerenderQueued = false;
  private rerenderQueueEpoch = 0;
  private rerenderRecoveryTimeout?: number;
  private textLayerRenderTasks = new Map<number, ITextLayerRenderTask>();
  private textSelectionCleanupFunctions = new Map<number, () => void>();
  private canvas: HTMLCanvasElement | undefined;
  private ctx: CanvasRenderingContext2D | undefined;
  private viewerMain: HTMLElement | null = null;
  private wheelEventTarget: HTMLElement | null = null;
  private resizeObserver?: ResizeObserver;
  private intersectionObserver?: IntersectionObserver;
  private thumbnailObserver?: IntersectionObserver;
  private thumbnailObserverRoot?: HTMLElement;
  private scrollThrottleTimeout?: number;
  private viewportDimensions = { width: 0, height: 0 };
  private viewportMode: 'auto' | 'page-fit' | 'page-width' | 'custom' = 'auto';
  private readonly MANUAL_MIN_ZOOM = 0.5;
  private readonly MANUAL_MAX_ZOOM = 3;
  private readonly ABSOLUTE_MIN_ZOOM = 0.1;
  private readonly ABSOLUTE_MAX_ZOOM = 4;
  private readonly PAGE_GAP = 20;
  private readonly RENDER_BUFFER = 3;

  constructor() {
    super();
  }

  public render(): TemplateResult {
    return html`
      <dees-tile .frameless=${this.frameless} class="${this.showSidebar ? 'with-sidebar' : ''} sidebar-${this.sidebarPosition} ${this.galleryActive ? 'gallery-mode' : ''}">
        ${this.showToolbar ? html`
          <div slot="header" class="toolbar">
            <dees-input-multitoggle size="sm" label="PDF view" labelPosition="none"
              .options=${[{ key: 'document', label: 'Document' }, { key: 'gallery', label: 'Gallery' }]}
              .selectedOption=${this.viewMode}
              @option-activate=${(event: CustomEvent<{ value: 'document' | 'gallery' }>) => this.viewMode = event.detail.value}></dees-input-multitoggle>
            <div class="toolbar-group" ?hidden=${this.viewMode === 'gallery'}>
              <button
                class="toolbar-button"
                aria-label="Previous page" title="Previous page" @click=${this.previousPage}
                ?disabled=${this.currentPage <= 1}
              >
                <dees-icon icon="lucide:ChevronLeft"></dees-icon>
              </button>
              <div class="page-info">
                <input
                  type="number"
                  aria-label="Page number"
                  min="1"
                  max="${this.totalPages}"
                  .value=${String(this.currentPage)}
                  @change=${this.handlePageInput}
                  class="page-input"
                />
                <span class="page-separator">/</span>
                <span class="page-total">${this.totalPages}</span>
              </div>
              <button
                class="toolbar-button"
                aria-label="Next page" title="Next page" @click=${this.nextPage}
                ?disabled=${this.currentPage >= this.totalPages}
              >
                <dees-icon icon="lucide:ChevronRight"></dees-icon>
              </button>
            </div>

            <div class="toolbar-group" ?hidden=${this.viewMode === 'gallery'}>
              <button
                class="toolbar-button"
                aria-label="Zoom out" title="Zoom out" @click=${this.zoomOut}
                ?disabled=${!this.canZoomOut}
              >
                <dees-icon icon="lucide:ZoomOut"></dees-icon>
              </button>
              <button
                class="toolbar-button"
                aria-label="Reset zoom" title="Reset zoom" @click=${this.resetZoom}
              >
                <span class="zoom-level">${Math.round(this.currentZoom * 100)}%</span>
              </button>
              <button
                class="toolbar-button"
                aria-label="Zoom in" title="Zoom in" @click=${this.zoomIn}
                ?disabled=${!this.canZoomIn}
              >
                <dees-icon icon="lucide:ZoomIn"></dees-icon>
              </button>
            </div>

            <div class="toolbar-group" ?hidden=${this.viewMode === 'gallery'}>
              <button
                class="toolbar-button"
                @click=${this.fitToPage}
                title="Fit to page"
              >
                <dees-icon icon="lucide:Maximize"></dees-icon>
              </button>
              <button
                class="toolbar-button"
                @click=${this.fitToWidth}
                title="Fit to width"
              >
                <dees-icon icon="lucide:ArrowLeftRight"></dees-icon>
              </button>
            </div>

            <div class="toolbar-group toolbar-group--end">
              <button
                class="toolbar-button"
                ?hidden=${this.viewMode === 'gallery'} @click=${() => this.showSidebar = !this.showSidebar}
                title="${this.showSidebar ? 'Hide thumbnails' : 'Show thumbnails'}"
              >
                <dees-icon icon="${this.showSidebar ? 'lucide:SidebarClose' : 'lucide:Sidebar'}"></dees-icon>
              </button>
              <button
                class="toolbar-button"
                @click=${this.downloadPdf}
                title="Download"
              >
                <dees-icon icon="lucide:Download"></dees-icon>
              </button>
              <button
                class="toolbar-button"
                @click=${this.printPdf}
                title="Print"
              >
                <dees-icon icon="lucide:Printer"></dees-icon>
              </button>
            </div>
          </div>
        ` : ''}

        <div class="viewer-container">
          ${this.thumbnailsVisible && !this.loading && !this.loadError ? html`
            <div class="sidebar">
              <div class="sidebar-header">
                <span>${this.viewMode === 'gallery' ? `${this.totalPages} pages` : 'Pages'}</span>
                <button
                  class="sidebar-close"
                  aria-label="Close page thumbnails"
                  ?hidden=${this.viewMode === 'gallery'}
                  @click=${() => this.showSidebar = false}
                >
                  <dees-icon icon="lucide:X"></dees-icon>
                </button>
              </div>
              <div class="sidebar-content">
                ${directives.keyed(this.documentId, html`
                  ${directives.repeat(
                    this.thumbnailData,
                    (item) => item.page,
                    (item) => html`
                      <button type="button" aria-label=${`Page ${item.page}`} aria-current=${this.currentPage === item.page ? 'page' : 'false'}
                        class="thumbnail ${this.currentPage === item.page ? 'active' : ''}"
                        data-page="${item.page}"
                        @click=${this.handleThumbnailClick}
                      >
                        <canvas class="thumbnail-canvas" data-page="${item.page}"></canvas>
                        <span class="thumbnail-number">${item.page}</span>
                      </button>
                    `
                  )}
                `)}
              </div>
            </div>
          ` : ''}

          <div class="viewer-main" tabindex="0" aria-label="PDF document" ?inert=${this.galleryActive} aria-hidden=${this.galleryActive} @scroll=${this.handleScroll}>
            ${this.loadError ? html`<div class="loading-container" role="alert"><dees-icon icon="lucide:FileWarning"></dees-icon><span>${this.loadError}</span><button type="button" @click=${() => this.schedulePdfLoad(this.connectionEpoch)}>Try again</button></div>` : this.loading ? html`
              <div class="loading-container">
                <div class="loading-spinner"></div>
                <div class="loading-text">Loading PDF...</div>
              </div>
            ` : html`
              <div class="pages-container">
                ${directives.repeat(
                  this.pageData,
                  (item) => item.page,
                  (item) => html`
                    <div class="page-wrapper" data-page="${item.page}">
                      <div class="canvas-container">
                        <canvas class="page-canvas" data-page="${item.page}"></canvas>
                        <div class="text-layer" data-page="${item.page}"></div>
                      </div>
                    </div>
                  `
                )}
              </div>
            `}
          </div>
        </div>
        ${this.showFooter ? html`<div slot="footer" class="pdf-footer">
          <div class="pdf-footer-left">
            <span class="pdf-footer-item">Zoom ${Math.round(this.currentZoom * 100)}%</span>
            ${this.pdfFileSize > 0 ? html`
              <span class="pdf-footer-item">${this.formatFileSize(this.pdfFileSize)}</span>
            ` : ''}
          </div>
          <div class="pdf-footer-center" style="margin-left: ${this.showSidebar && this.sidebarPosition === 'left' ? '100px' : this.showSidebar && this.sidebarPosition === 'right' ? '-100px' : '0'}">
            <span>Page ${this.currentPage} of ${this.totalPages}</span>
          </div>
          <div class="pdf-footer-right">
            ${this.pdfUrl ? html`
              <span class="pdf-footer-filename">${this.filename || (this.pdfUrl.startsWith('blob:') || this.pdfUrl.startsWith('data:') ? '' : this.pdfUrl.split('/').pop())}</span>
            ` : ''}
          </div>
        </div>` : ''}
      </dees-tile>
    `;
  }

  public async connectedCallback() {
    const connectionEpoch = ++this.connectionEpoch;
    await super.connectedCallback();
    await this.updateComplete;
    if (!this.isConnected || connectionEpoch !== this.connectionEpoch) return;
    this.ensureViewerRefs();
    const operationKey = `${connectionEpoch}:${this.pdfUrl}`;
    if (this.loadOperation?.key === operationKey) {
      await this.loadOperation.promise;
    } else if (connectionEpoch > 1 || !this.pdfDocument) {
      await this.schedulePdfLoad(connectionEpoch);
    }
  }

  public async disconnectedCallback() {
    this.connectionEpoch += 1;
    this.loadGeneration += 1;
    this.renderAdmissionOpen = false;
    this.renderAbortController?.abort();
    const cleanupPromise = this.cleanupDocument();
    await super.disconnectedCallback();
    await cleanupPromise;
    this.renderState = 'disposed';
    this.canvas = undefined;
    this.ctx = undefined;
  }

  public async updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties);

    if (changedProperties.has('pdfUrl') && this.isConnected) {
      await this.schedulePdfLoad(this.connectionEpoch);
    }

    if ((changedProperties.has('showSidebar') || changedProperties.has('viewMode')) && !this.thumbnailsVisible) {
      this.thumbnailRenderGeneration += 1;
      this.thumbnailRenderQueued = false;
      for (const task of this.thumbnailRenderTasks) {
        try {
          task.cancel();
        } catch {
          // The scheduler still owns each task promise until it settles.
        }
      }
    }

    // Re-render thumbnails when sidebar becomes visible and document is loaded
    if (
      (changedProperties.has('showSidebar') || changedProperties.has('viewMode'))
      && this.thumbnailsVisible
      && this.renderAdmissionOpen
      && this.pdfDocument
    ) {
      const connectionEpoch = this.connectionEpoch;
      const loadGeneration = this.loadGeneration;
      const pdfDocument = this.pdfDocument;
      await waitForAnimationFrame();
      if (
        !this.isConnected
        || connectionEpoch !== this.connectionEpoch
        || loadGeneration !== this.loadGeneration
        || pdfDocument !== this.pdfDocument
        || !this.renderAdmissionOpen
        || !this.thumbnailsVisible
      ) return;
      await this.renderThumbnails();
      if (
        !this.isConnected
        || connectionEpoch !== this.connectionEpoch
        || loadGeneration !== this.loadGeneration
        || pdfDocument !== this.pdfDocument
        || !this.renderAdmissionOpen
        || !this.thumbnailsVisible
      ) return;
      // Re-setup intersection observer for lazy loading of pages
      this.setupIntersectionObserver();
      // Scroll to active thumbnail after rendering
      this.scrollThumbnailIntoView(this.currentPage);
      if (changedProperties.has('viewMode') && this.viewMode === 'gallery') {
        this.shadowRoot?.querySelector<HTMLButtonElement>(`.thumbnail[data-page="${this.currentPage}"]`)?.focus({ preventScroll: true });
      }
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private schedulePdfLoad(
    connectionEpochArg: number,
    renderRecoveryStateArg?: IRenderRecoveryState,
  ): Promise<void> {
    const url = this.pdfUrl;
    const baseKey = `${connectionEpochArg}:${url}`;
    if (!renderRecoveryStateArg && this.loadOperation?.key === baseKey) {
      return this.loadOperation.promise;
    }
    const generation = ++this.loadGeneration;
    const key = renderRecoveryStateArg
      ? `${baseKey}:render-recovery:${generation}`
      : baseKey;
    const operation: ILoadOperation = {
      key,
      promise: Promise.resolve(),
    };
    operation.promise = this.performPdfLoad(
      generation,
      connectionEpochArg,
      url,
      renderRecoveryStateArg,
    )
      .finally(() => {
        if (this.loadOperation === operation) {
          this.loadOperation = null;
        }
      });
    this.loadOperation = operation;
    return operation.promise;
  }

  private isCurrentLoad(generationArg: number, connectionEpochArg: number, urlArg: string): boolean {
    return this.isConnected
      && generationArg === this.loadGeneration
      && connectionEpochArg === this.connectionEpoch
      && urlArg === this.pdfUrl;
  }

  private async performPdfLoad(
    generationArg: number,
    connectionEpochArg: number,
    urlArg: string,
    renderRecoveryStateArg?: IRenderRecoveryState,
  ): Promise<void> {
    await this.cleanupDocument();
    if (!this.isCurrentLoad(generationArg, connectionEpochArg, urlArg)) {
      return;
    }
    if (!urlArg) {
      this.loading = false;
      this.loadError = '';
      this.renderState = 'idle';
      return;
    }

    this.loading = true;
    this.loadError = '';
    this.renderState = 'loading';
    const abortController = new AbortController();
    this.renderAbortController = abortController;
    const { signal } = abortController;

    try {
      const pdfLoadPromise = PdfManager.loadDocument(urlArg, signal);
      this.pdfLoadPromise = pdfLoadPromise;
      let pdfDocument: PDFDocumentProxy;
      try {
        pdfDocument = await pdfLoadPromise;
      } finally {
        if (this.pdfLoadPromise === pdfLoadPromise) {
          this.pdfLoadPromise = null;
        }
      }
      if (!this.isCurrentLoad(generationArg, connectionEpochArg, urlArg) || signal.aborted) {
        await PdfManager.releaseDocument(pdfDocument);
        return;
      }
      this.pdfDocument = pdfDocument;
      this.renderAdmissionOpen = true;
      this.documentId = `${urlArg}-${generationArg}`;
      const documentUsePipeline = (async () => {
        this.totalPages = pdfDocument.numPages;
        if (renderRecoveryStateArg) {
          this.currentPage = Math.min(
            Math.max(renderRecoveryStateArg.currentPage, 1),
            this.totalPages,
          );
          this.currentZoom = renderRecoveryStateArg.currentZoom;
          this.viewportMode = renderRecoveryStateArg.viewportMode;
        } else {
          this.currentPage = this.initialPage;
          this.resolveInitialViewportMode();
        }

        try {
          const data = await raceWithAbort(pdfDocument.getData(), signal);
          if (!this.isCurrentLoad(generationArg, connectionEpochArg, urlArg)) return;
          this.pdfFileSize = data.length;
        } catch {
          if (!this.isCurrentLoad(generationArg, connectionEpochArg, urlArg)) return;
          this.pdfFileSize = 0;
        }

        this.thumbnailData = Array.from({length: this.totalPages}, (_, i) => ({
          page: i + 1,
          rendered: false
        }));
        this.pageData = Array.from({length: this.totalPages}, (_, i) => ({
          page: i + 1,
          rendered: false,
          rendering: false,
          textLayerRendered: false,
        }));

        this.loading = false;
        await this.updateComplete;
        if (!this.isCurrentLoad(generationArg, connectionEpochArg, urlArg)) return;
        this.ensureViewerRefs();
        this.setupIntersectionObserver();

        await waitForAnimationFrame(signal);
        if (!this.isCurrentLoad(generationArg, connectionEpochArg, urlArg) || signal.aborted) return;
        this.renderState = 'rendering-main';

        await this.renderVisiblePages();
        if (!this.isCurrentLoad(generationArg, connectionEpochArg, urlArg) || signal.aborted) return;
        if (this.currentPage > 1) {
          await this.scrollToPage(this.currentPage, false);
        }

        if (this.thumbnailsVisible) {
          await this.updateComplete;
          await waitForAnimationFrame(signal);
          if (!this.isCurrentLoad(generationArg, connectionEpochArg, urlArg) || signal.aborted) return;
          await this.renderThumbnails();
          if (!this.isCurrentLoad(generationArg, connectionEpochArg, urlArg) || signal.aborted) return;
        }

        if (this.isCurrentLoad(generationArg, connectionEpochArg, urlArg)) {
          this.renderState = 'rendered';
        }
      })();
      this.documentUsePipelines.add(documentUsePipeline);
      try {
        await documentUsePipeline;
      } finally {
        this.documentUsePipelines.delete(documentUsePipeline);
      }
    } catch (error) {
      if (this.isCurrentLoad(generationArg, connectionEpochArg, urlArg) && !signal.aborted) {
        console.error('Error loading PDF:', error);
        this.loading = false;
        this.renderState = 'error';
        this.loadError = 'Could not open this PDF.';
      }
    }
  }

  private setupIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const pageWrapper = entry.target as HTMLElement;
          const pageNum = parseInt(pageWrapper.dataset.page || '1');

          if (entry.isIntersecting) {
            this.renderPageIfNeeded(pageNum);
          }
        }
      },
      {
        root: this.viewerMain,
        rootMargin: `${this.RENDER_BUFFER * 100}px 0px`,
        threshold: 0.01
      }
    );

    // Observe all page wrappers
    const pageWrappers = this.shadowRoot?.querySelectorAll('.page-wrapper');
    if (pageWrappers) {
      pageWrappers.forEach(wrapper => {
        this.intersectionObserver?.observe(wrapper);
      });
    }
  }

  private async renderVisiblePages() {
    if (!this.viewerMain) return;

    // Find visible pages based on scroll position
    const clientHeight = this.viewerMain.clientHeight;

    for (const pageInfo of this.pageData) {
      const pageWrapper = this.shadowRoot?.querySelector(`.page-wrapper[data-page="${pageInfo.page}"]`) as HTMLElement;
      if (!pageWrapper) continue;

      const rect = pageWrapper.getBoundingClientRect();
      const viewerRect = this.viewerMain.getBoundingClientRect();
      const relativeTop = rect.top - viewerRect.top;
      const relativeBottom = relativeTop + rect.height;

      // Check if page is visible or within buffer zone
      const buffer = this.RENDER_BUFFER * clientHeight;
      if (relativeBottom >= -buffer && relativeTop <= clientHeight + buffer) {
        await this.renderPageIfNeeded(pageInfo.page);
      }
    }
  }

  private renderPageIfNeeded(pageNum: number): Promise<void> {
    const pageInfo = this.pageData.find(p => p.page === pageNum);
    const pdfDocument = this.pdfDocument;
    const signal = this.renderAbortController?.signal;
    if (
      !this.renderAdmissionOpen
      || !pdfDocument
      || !signal
      || !pageInfo
      || pageInfo.rendered
      || pageInfo.rendering
    ) return Promise.resolve();

    const renderGeneration = this.renderGeneration;
    const isCurrentRender = (): boolean => (
      this.isConnected
      && this.renderAdmissionOpen
      && !signal.aborted
      && renderGeneration === this.renderGeneration
      && pdfDocument === this.pdfDocument
    );
    pageInfo.rendering = true;
    const renderPipeline = (async () => {
      let page: PDFPageProxy | undefined;
      let renderTask: RenderTask | undefined;
      try {
        const canvas = this.shadowRoot?.querySelector(
          `.page-canvas[data-page="${pageNum}"]`,
        ) as HTMLCanvasElement;
        if (!canvas) {
          pageInfo.rendering = false;
          return;
        }

        page = await raceWithAbort(pdfDocument.getPage(pageNum), signal);
        if (!isCurrentRender()) return;
        const viewport = this.computeViewport(page);

        // Set canvas dimensions with device pixel ratio for sharp rendering
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          pageInfo.rendering = false;
          return;
        }

        // Scale context for high-DPI displays
        ctx.scale(dpr, dpr);

        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
        };

        renderTask = page.render(renderContext);
        this.pageRenderTasks.set(pageNum, renderTask);
        await raceWithAbort(renderTask.promise, signal);
        if (!isCurrentRender()) return;
        pageInfo.rendered = true;
        pageInfo.rendering = false;
        if (this.pageRenderTasks.get(pageNum) === renderTask) {
          this.pageRenderTasks.delete(pageNum);
        }

        // Render text layer for selection
        await this.renderTextLayer(pageNum, renderGeneration, pdfDocument, signal);
        if (!isCurrentRender()) return;

        // Update page data to reflect rendered state
        this.requestUpdate('pageData');
      } catch (error: unknown) {
        if (
          !(error instanceof Error)
          || !['AbortError', 'AbortException', 'RenderingCancelledException'].includes(error.name)
        ) {
          console.error(`Error rendering page ${pageNum}:`, error);
        }
        if (renderGeneration === this.renderGeneration) {
          pageInfo.rendering = false;
        }
      } finally {
        if (renderTask && this.pageRenderTasks.get(pageNum) === renderTask) {
          this.pageRenderTasks.delete(pageNum);
        }
        page?.cleanup();
      }
    })();
    this.pageRenderPipelines.add(renderPipeline);
    void renderPipeline.finally(() => {
      this.pageRenderPipelines.delete(renderPipeline);
    }).catch(() => {});
    return renderPipeline;
  }

  private async renderTextLayer(
    pageNum: number,
    renderGenerationArg: number,
    pdfDocumentArg: PDFDocumentProxy,
    signalArg: AbortSignal,
  ): Promise<void> {
    const pageInfo = this.pageData.find(p => p.page === pageNum);
    if (
      renderGenerationArg !== this.renderGeneration
      || signalArg.aborted
      || pdfDocumentArg !== this.pdfDocument
      || !pageInfo
      || pageInfo.textLayerRendered
    ) return;

    let page: PDFPageProxy | undefined;
    let renderTask: ITextLayerRenderTask | undefined;
    try {
      const textLayerDiv = this.shadowRoot?.querySelector(
        `.text-layer[data-page="${pageNum}"]`
      ) as HTMLElement;
      if (!textLayerDiv) return;

      this.textSelectionCleanupFunctions.get(pageNum)?.();
      this.textSelectionCleanupFunctions.delete(pageNum);
      textLayerDiv.innerHTML = '';

      page = await raceWithAbort(pdfDocumentArg.getPage(pageNum), signalArg);
      if (
        renderGenerationArg !== this.renderGeneration
        || signalArg.aborted
        || pdfDocumentArg !== this.pdfDocument
      ) return;
      const textContent = await raceWithAbort(page.getTextContent(), signalArg);
      if (
        renderGenerationArg !== this.renderGeneration
        || signalArg.aborted
        || pdfDocumentArg !== this.pdfDocument
      ) return;
      const viewport = this.computeViewport(page);

      textLayerDiv.style.width = `${viewport.width}px`;
      textLayerDiv.style.height = `${viewport.height}px`;

      // Set the scale factor CSS variable - required by PDF.js text layer
      textLayerDiv.style.setProperty('--scale-factor', String(viewport.scale));

      const textLayer = new TextLayer({
        textContentSource: textContent,
        container: textLayerDiv,
        viewport: viewport,
      });
      const textLayerRenderPromise = textLayer.render();
      renderTask = {
        textLayer,
        promise: textLayerRenderPromise,
      };
      this.textLayerRenderTasks.set(pageNum, renderTask);
      await raceWithAbort(textLayerRenderPromise, signalArg);
      if (
        renderGenerationArg !== this.renderGeneration
        || signalArg.aborted
        || pdfDocumentArg !== this.pdfDocument
      ) return;

      // Add endOfContent for selection boundary
      const endOfContent = document.createElement('div');
      endOfContent.className = 'endOfContent';
      textLayerDiv.appendChild(endOfContent);

      // Custom drag selection for Shadow DOM compatibility
      // caretRangeFromPoint doesn't pierce shadow DOM, so we find spans manually
      let isDragging = false;
      let anchorNode: Node | null = null;
      let anchorOffset = 0;

      const getTextPositionFromPoint = (x: number, y: number): { node: Node; offset: number } | null => {
        // Find span at coordinates by checking bounding rects
        const spans = Array.from(textLayerDiv.querySelectorAll('span'));
        for (const span of spans) {
          const rect = span.getBoundingClientRect();
          if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
            const textNode = span.firstChild;
            if (textNode && textNode.nodeType === Node.TEXT_NODE) {
              // Calculate character offset based on x position
              const text = textNode.textContent || '';
              const charWidth = rect.width / text.length;
              const relativeX = x - rect.left;
              const offset = Math.min(Math.round(relativeX / charWidth), text.length);
              return { node: textNode, offset };
            }
          }
        }
        return null;
      };

      const handleMouseUp = () => {
        if (isDragging) {
          isDragging = false;
          anchorNode = null;
          textLayerDiv.classList.remove('selecting');
        }
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('mousemove', handleMouseMove);
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging || !anchorNode) return;

        e.preventDefault();
        const pos = getTextPositionFromPoint(e.clientX, e.clientY);
        if (pos) {
          const selection = window.getSelection();
          if (selection) {
            try {
              selection.setBaseAndExtent(anchorNode, anchorOffset, pos.node, pos.offset);
            } catch (err) {
              // Ignore errors from invalid selections
            }
          }
        }
      };

      const handleMouseDown = (e: MouseEvent) => {
        if (e.button !== 0) return;

        const pos = getTextPositionFromPoint(e.clientX, e.clientY);
        if (pos) {
          // Prevent native selection behavior
          e.preventDefault();

          isDragging = true;
          anchorNode = pos.node;
          anchorOffset = pos.offset;
          textLayerDiv.classList.add('selecting');

          // Clear existing selection
          const selection = window.getSelection();
          selection?.removeAllRanges();

          // Add document-level listeners for drag
          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }
      };
      textLayerDiv.addEventListener('mousedown', handleMouseDown);
      this.textSelectionCleanupFunctions.set(pageNum, () => {
        textLayerDiv.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('mousemove', handleMouseMove);
      });

      pageInfo.textLayerRendered = true;
    } catch (error: unknown) {
      if (
        !(error instanceof Error)
        || !['AbortError', 'AbortException', 'RenderingCancelledException'].includes(error.name)
      ) {
        console.error(`Error rendering text layer for page ${pageNum}:`, error);
      }
    } finally {
      if (renderTask && this.textLayerRenderTasks.get(pageNum) === renderTask) {
        this.textLayerRenderTasks.delete(pageNum);
      }
      page?.cleanup();
    }
  }

  private handleScroll = () => {
    // Throttle scroll events
    if (this.scrollThrottleTimeout) {
      clearTimeout(this.scrollThrottleTimeout);
    }

    this.scrollThrottleTimeout = window.setTimeout(() => {
      this.updateCurrentPage();
      this.renderVisiblePages();
    }, 50);
  }

  private updateCurrentPage() {
    if (!this.viewerMain) return;

    const scrollTop = this.viewerMain.scrollTop;
    const clientHeight = this.viewerMain.clientHeight;
    const centerY = scrollTop + clientHeight / 2;

    // Find which page is at the center of the viewport
    for (let i = 0; i < this.pageData.length; i++) {
      const pageWrapper = this.shadowRoot?.querySelector(`.page-wrapper[data-page="${i + 1}"]`) as HTMLElement;
      if (!pageWrapper) continue;

      const rect = pageWrapper.getBoundingClientRect();
      const viewerRect = this.viewerMain.getBoundingClientRect();
      const relativeTop = rect.top - viewerRect.top + scrollTop;
      const relativeBottom = relativeTop + rect.height;

      if (centerY >= relativeTop && centerY <= relativeBottom) {
        if (this.currentPage !== i + 1) {
          this.currentPage = i + 1;
          // Scroll the thumbnail into view if sidebar is visible
          if (this.showSidebar) {
            this.scrollThumbnailIntoView(i + 1);
          }
        }
        break;
      }
    }
  }

  private scrollThumbnailIntoView(pageNum: number) {
    const thumbnail = this.shadowRoot?.querySelector(`.thumbnail[data-page="${pageNum}"]`) as HTMLElement;
    const sidebarContent = this.shadowRoot?.querySelector('.sidebar-content') as HTMLElement;

    if (thumbnail && sidebarContent) {
      // Get the thumbnail's position relative to the sidebar
      const thumbnailRect = thumbnail.getBoundingClientRect();
      const sidebarRect = sidebarContent.getBoundingClientRect();

      // Check if thumbnail is outside the visible area
      const isAbove = thumbnailRect.top < sidebarRect.top;
      const isBelow = thumbnailRect.bottom > sidebarRect.bottom;

      if (isAbove || isBelow) {
        // Calculate the scroll position to center the thumbnail
        const thumbnailOffset = thumbnail.offsetTop;
        const thumbnailHeight = thumbnail.offsetHeight;
        const sidebarHeight = sidebarContent.clientHeight;
        const targetScrollTop = thumbnailOffset - (sidebarHeight / 2) + (thumbnailHeight / 2);

        // Scroll the sidebar to center the thumbnail
        sidebarContent.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth'
        });
      }
    }
  }

  private async scrollToPage(pageNum: number, smooth: boolean = true) {
    await this.updateComplete;
    const pageWrapper = this.shadowRoot?.querySelector(`.page-wrapper[data-page="${pageNum}"]`) as HTMLElement;
    if (pageWrapper && this.viewerMain) {
      // Calculate the offset of the page wrapper relative to the viewer
      const pageRect = pageWrapper.getBoundingClientRect();
      const viewerRect = this.viewerMain.getBoundingClientRect();
      const currentScrollTop = this.viewerMain.scrollTop;

      // Calculate the target scroll position (offset by 16px so page doesn't touch the top edge)
      const targetScrollTop = currentScrollTop + (pageRect.top - viewerRect.top) - this.viewerMain.clientTop - 16;

      // Scroll to the calculated position
      if (smooth) {
        this.viewerMain.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });
      } else {
        this.viewerMain.scrollTop = targetScrollTop;
      }

      // Update current page
      this.currentPage = pageNum;

      // Scroll thumbnail into view if sidebar is visible
      if (this.showSidebar) {
        this.scrollThumbnailIntoView(pageNum);
      }

      // Ensure the page is rendered
      await this.renderPageIfNeeded(pageNum);
    }
  }

  private renderThumbnails(): Promise<void> {
    if (!this.renderAdmissionOpen || !this.thumbnailsVisible || !this.pdfDocument) {
      return Promise.resolve();
    }
    if (this.thumbnailRenderPromise) {
      this.thumbnailRenderGeneration += 1;
      this.thumbnailRenderQueued = true;
      for (const task of this.thumbnailRenderTasks) {
        try {
          task.cancel();
        } catch {
          // The active queue iteration still awaits the task promise.
        }
      }
      return this.thumbnailRenderPromise;
    }
    this.thumbnailRenderGeneration += 1;
    const thumbnailRenderQueueEpoch = ++this.thumbnailRenderQueueEpoch;
    const renderPromise = (async () => {
      do {
        if (thumbnailRenderQueueEpoch !== this.thumbnailRenderQueueEpoch) return;
        this.thumbnailRenderQueued = false;
        const renderGeneration = this.thumbnailRenderGeneration;
        await this.performRenderThumbnails(renderGeneration);
      } while (
        thumbnailRenderQueueEpoch === this.thumbnailRenderQueueEpoch
        && this.thumbnailRenderQueued
        && this.renderAdmissionOpen
        && this.thumbnailsVisible
        && this.pdfDocument
      );
    })();
    this.thumbnailRenderPromise = renderPromise;
    void renderPromise.finally(() => {
      if (this.thumbnailRenderPromise === renderPromise) {
        this.thumbnailRenderPromise = null;
      }
    }).catch(() => {});
    return renderPromise;
  }

  private observeThumbnails(root: HTMLElement, thumbnails: NodeListOf<HTMLElement>): void {
    if (this.thumbnailObserverRoot === root) return;
    this.thumbnailObserver?.disconnect();
    this.thumbnailObserverRoot = root;
    this.thumbnailObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) void this.renderThumbnails();
    }, { root, rootMargin: '240px 0px' });
    thumbnails.forEach(thumbnail => this.thumbnailObserver!.observe(thumbnail));
  }

  private async performRenderThumbnails(renderGenerationArg: number): Promise<void> {
    const pdfDocument = this.pdfDocument;
    if (!this.renderAdmissionOpen || !this.thumbnailsVisible || !pdfDocument) {
      return;
    }
    const isCurrentRender = (): boolean => (
      this.isConnected
      && this.renderAdmissionOpen
      && this.thumbnailsVisible
      && renderGenerationArg === this.thumbnailRenderGeneration
      && pdfDocument === this.pdfDocument
    );

    const signal = this.renderAbortController?.signal;
    if (!signal || signal.aborted) return;
    this.renderState = 'rendering-thumbs';

    // Cancel any existing thumbnail render tasks
    for (const task of this.thumbnailRenderTasks) {
      try {
        task.cancel();
      } catch (error) {
        // Ignore cancellation errors
      }
    }

    try {
      await this.updateComplete;
      if (!isCurrentRender()) return;
      const thumbnails = this.shadowRoot?.querySelectorAll('.thumbnail') as NodeListOf<HTMLElement>;
      const thumbnailCanvases = this.shadowRoot?.querySelectorAll('.thumbnail-canvas') as NodeListOf<HTMLCanvasElement>;
      const sidebarContent = this.shadowRoot?.querySelector('.sidebar-content') as HTMLElement;
      if (!sidebarContent) return;
      this.observeThumbnails(sidebarContent, thumbnails);
      const visibleBounds = sidebarContent.getBoundingClientRect();

      // Get the actual available width for thumbnails (sidebar width minus padding)
      const sidebarStyles = window.getComputedStyle(sidebarContent);
      const sidebarPadding = parseFloat(sidebarStyles.paddingLeft) + parseFloat(sidebarStyles.paddingRight);
      const maxThumbnailWidth = this.viewMode === 'gallery' ? Math.max(1, thumbnails[0]?.clientWidth || 160) : sidebarContent.clientWidth - sidebarPadding - 4;

      for (let i = 0; i < thumbnailCanvases.length; i++) {
        if (signal?.aborted || !isCurrentRender()) return;

        const canvas = thumbnailCanvases[i];
        const thumbnail = thumbnails[i];
        const thumbnailBounds = thumbnail.getBoundingClientRect();
        if (thumbnailBounds.bottom < visibleBounds.top - 240 || thumbnailBounds.top > visibleBounds.bottom + 240) continue;
        const pageNum = parseInt(canvas.dataset.page || '1');
        const previousThumbnail = this.thumbnailData.find(item => item.page === pageNum);
        if (previousThumbnail?.rendered && previousThumbnail.width === Math.round(maxThumbnailWidth) && canvas.width > 0 && canvas.style.width) continue;
        const page = await raceWithAbort(pdfDocument.getPage(pageNum), signal);
        try {
          if (!isCurrentRender()) return;
          const initialViewport = page.getViewport({ scale: 1 });
          const scale = maxThumbnailWidth / initialViewport.width;
          const viewport = page.getViewport({ scale });
          const dpr = window.devicePixelRatio || 1;
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;
          thumbnail.style.height = `${viewport.height + 4}px`;
          thumbnail.style.minHeight = `${viewport.height + 4}px`;

          const context = canvas.getContext('2d');
          if (!context) continue;
          context.scale(dpr, dpr);

          const renderTask = page.render({
            canvasContext: context,
            viewport,
          });
          this.thumbnailRenderTasks.add(renderTask);
          try {
            await raceWithAbort(renderTask.promise, signal);
          } finally {
            this.thumbnailRenderTasks.delete(renderTask);
          }
          if (!isCurrentRender()) return;

          const thumbData = this.thumbnailData.find(t => t.page === pageNum);
          if (thumbData) {
            thumbData.rendered = true;
            thumbData.width = Math.round(maxThumbnailWidth);
          }
        } finally {
          page.cleanup();
        }
      }

      // Trigger update to reflect rendered state
      if (isCurrentRender()) {
        this.requestUpdate('thumbnailData');
      }
    } catch (error: unknown) {
      // Only log non-cancellation errors
      if (
        !(error instanceof Error)
        || !['AbortError', 'AbortException', 'RenderingCancelledException'].includes(error.name)
      ) {
        console.error('Error rendering thumbnails:', error);
      }
    }
  }

  private previousPage() {
    if (this.currentPage > 1) {
      this.scrollToPage(this.currentPage - 1);
    }
  }

  private nextPage() {
    if (this.currentPage < this.totalPages) {
      this.scrollToPage(this.currentPage + 1);
    }
  }


  private async handleThumbnailClick(e: Event) {
    const target = e.currentTarget as HTMLElement;
    const pageNum = parseInt(target.dataset.page || '1');
    this.viewMode = 'document';
    await this.updateComplete;
    await this.scrollToPage(pageNum, false);
    this.shadowRoot?.querySelector<HTMLElement>('.viewer-main')?.focus({ preventScroll: true });
  }

  private handlePageInput(e: Event) {
    const input = e.target as HTMLInputElement;
    const pageNum = parseInt(input.value);
    this.scrollToPage(pageNum);
  }

  private zoomIn() {
    const nextZoom = Math.min(this.MANUAL_MAX_ZOOM, this.currentZoom * 1.2);
    this.viewportMode = 'custom';
    if (nextZoom !== this.currentZoom) {
      this.currentZoom = nextZoom;
      this.reRenderAllPages();
    }
  }

  private zoomOut() {
    const nextZoom = Math.max(this.MANUAL_MIN_ZOOM, this.currentZoom / 1.2);
    this.viewportMode = 'custom';
    if (nextZoom !== this.currentZoom) {
      this.currentZoom = nextZoom;
      this.reRenderAllPages();
    }
  }

  private resetZoom() {
    this.viewportMode = 'custom';
    this.currentZoom = 1;
    this.reRenderAllPages();
  }

  private fitToPage() {
    this.viewportMode = 'page-fit';
    this.reRenderAllPages();
  }

  private fitToWidth() {
    this.viewportMode = 'page-width';
    this.reRenderAllPages();
  }

  private reRenderAllPages() {
    this.renderGeneration += 1;
    // Clear all rendered pages to force re-render with new zoom
    this.pageData.forEach(page => {
      page.rendered = false;
      page.rendering = false;
      page.textLayerRendered = false;
    });

    // Cancel any ongoing render tasks
    this.pageRenderTasks.forEach(task => {
      try {
        task.cancel();
      } catch (error) {
        // Ignore cancellation errors
      }
    });
    this.pageRenderTasks.clear();

    // Cancel text layer render tasks
    this.textLayerRenderTasks.forEach(task => {
      try {
        task.textLayer.cancel();
      } catch (error) {
        // Ignore cancellation errors
      }
    });
    this.textLayerRenderTasks.clear();
    for (const cleanup of this.textSelectionCleanupFunctions.values()) {
      cleanup();
    }
    this.textSelectionCleanupFunctions.clear();

    // Request update to re-render pages
    this.requestUpdate();

    this.rerenderQueued = true;
    if (this.rerenderPromise) return;

    const rerenderQueueEpoch = ++this.rerenderQueueEpoch;
    const rerenderPromise = (async () => {
      while (
        rerenderQueueEpoch === this.rerenderQueueEpoch
        && this.rerenderQueued
        && this.renderAdmissionOpen
        && this.isConnected
      ) {
        this.rerenderQueued = false;
        const renderGeneration = this.renderGeneration;
        const priorRenderPipelines = [...this.pageRenderPipelines];
        const pipelinesSettled = await settlePromisesWithin(
          priorRenderPipelines,
          pipelineSettlementGraceMs,
        );
        if (!pipelinesSettled) {
          if (!this.rerenderRecoveryTimeout) {
            const connectionEpoch = this.connectionEpoch;
            const loadGeneration = this.loadGeneration;
            const url = this.pdfUrl;
            this.rerenderRecoveryTimeout = window.setTimeout(() => {
              this.rerenderRecoveryTimeout = undefined;
              if (
                rerenderQueueEpoch !== this.rerenderQueueEpoch
                || connectionEpoch !== this.connectionEpoch
                || loadGeneration !== this.loadGeneration
                || url !== this.pdfUrl
                || !this.renderAdmissionOpen
                || !this.isConnected
              ) return;
              void this.schedulePdfLoad(connectionEpoch, {
                currentPage: this.currentPage,
                currentZoom: this.currentZoom,
                viewportMode: this.viewportMode,
              });
            }, 0);
          }
          return;
        }
        await this.updateComplete;
        if (
          rerenderQueueEpoch === this.rerenderQueueEpoch
          && renderGeneration === this.renderGeneration
          && !this.rerenderQueued
          && this.renderAdmissionOpen
          && this.isConnected
        ) {
          await this.renderVisiblePages();
        }
      }
    })();
    this.rerenderPromise = rerenderPromise;
    void rerenderPromise.finally(() => {
      if (this.rerenderPromise === rerenderPromise) {
        this.rerenderPromise = null;
      }
    }).catch(() => {});
  }

  private async downloadPdf() {
    const pdfDocument = this.pdfDocument;
    const signal = this.renderAbortController?.signal;
    if (!pdfDocument || !signal) return;
    const generation = this.loadGeneration;
    const connectionEpoch = this.connectionEpoch;
    const url = this.pdfUrl;

    let ownedBlobUrl: string | undefined;
    try {
      // Get raw PDF data from the loaded document
      const data = await raceWithAbort(pdfDocument.getData(), signal);
      if (
        !this.isCurrentLoad(generation, connectionEpoch, url)
        || signal.aborted
        || pdfDocument !== this.pdfDocument
      ) return;
      const blob = new Blob([new Uint8Array(data)], { type: 'application/pdf' });
      ownedBlobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = ownedBlobUrl;
      link.download = this.filename || (url.startsWith('blob:') || url.startsWith('data:') ? 'document.pdf' : url.split('/').pop()?.split(/[?#]/)[0] || 'document.pdf');
      link.click();

      // Clean up blob URL after short delay
      const scheduledBlobUrl = ownedBlobUrl;
      setTimeout(() => URL.revokeObjectURL(scheduledBlobUrl), 1000);
      ownedBlobUrl = undefined;
    } catch (error) {
      if (!(error instanceof Error) || error.name !== 'AbortError') {
        console.error('Error downloading PDF:', error);
      }
    } finally {
      if (ownedBlobUrl) URL.revokeObjectURL(ownedBlobUrl);
    }
  }

  private async printPdf() {
    const pdfDocument = this.pdfDocument;
    const signal = this.renderAbortController?.signal;
    if (!pdfDocument || !signal) return;
    const generation = this.loadGeneration;
    const connectionEpoch = this.connectionEpoch;
    const url = this.pdfUrl;

    let ownedPdfUrl: string | undefined;
    let ownedHtmlUrl: string | undefined;
    try {
      // Get raw PDF data from the loaded document
      const data = await raceWithAbort(pdfDocument.getData(), signal);
      if (
        !this.isCurrentLoad(generation, connectionEpoch, url)
        || signal.aborted
        || pdfDocument !== this.pdfDocument
      ) return;
      const blob = new Blob([new Uint8Array(data)], { type: 'application/pdf' });
      ownedPdfUrl = URL.createObjectURL(blob);

      // Create an HTML wrapper page that embeds the PDF and handles print/close
      // This gives us control over the afterprint event (direct PDF URLs don't support it)
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Print PDF</title>
          <style>
            * { margin: 0; padding: 0; }
            html, body { width: 100%; height: 100%; overflow: hidden; }
            iframe { width: 100%; height: 100%; border: none; }
            @media print {
              html, body, iframe { width: 100%; height: 100%; }
            }
          </style>
        </head>
        <body>
          <iframe src="${ownedPdfUrl}" type="application/pdf"></iframe>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.focus();
                window.print();
              }, 500);
            };
            window.onafterprint = function() {
              window.close();
            };
            // Safety close after 2 minutes
            setTimeout(function() { window.close(); }, 120000);
          </script>
        </body>
        </html>
      `;
      const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
      ownedHtmlUrl = URL.createObjectURL(htmlBlob);

      const printWindow = window.open(ownedHtmlUrl, '_blank', 'width=800,height=600');
      if (printWindow) {
        const scheduledPdfUrl = ownedPdfUrl;
        const scheduledHtmlUrl = ownedHtmlUrl;
        let urlsRevoked = false;
        const revokeUrls = (): void => {
          if (urlsRevoked) return;
          urlsRevoked = true;
          URL.revokeObjectURL(scheduledPdfUrl);
          URL.revokeObjectURL(scheduledHtmlUrl);
        };
        // Cleanup blob URLs when window closes
        const checkClosed = setInterval(() => {
          if (printWindow.closed) {
            clearInterval(checkClosed);
            revokeUrls();
          }
        }, 500);
        // Safety cleanup after 2 minutes
        setTimeout(() => {
          clearInterval(checkClosed);
          revokeUrls();
        }, 120000);
        ownedPdfUrl = undefined;
        ownedHtmlUrl = undefined;
      } else {
        // Popup blocked - fall back to direct navigation
        window.open(ownedPdfUrl, '_blank');
        const scheduledPdfUrl = ownedPdfUrl;
        setTimeout(() => URL.revokeObjectURL(scheduledPdfUrl), 60000);
        ownedPdfUrl = undefined;
        URL.revokeObjectURL(ownedHtmlUrl);
        ownedHtmlUrl = undefined;
      }
    } catch (error) {
      if (!(error instanceof Error) || error.name !== 'AbortError') {
        console.error('Error printing PDF:', error);
      }
    } finally {
      if (ownedPdfUrl) URL.revokeObjectURL(ownedPdfUrl);
      if (ownedHtmlUrl) URL.revokeObjectURL(ownedHtmlUrl);
    }
  }

  /**
   * Provide context menu items for right-click functionality
   */
  public getContextMenuItems() {
    const items: any[] = [];

    // Add copy option if text is selected
    const selection = window.getSelection();
    const selectedText = selection?.toString() || '';
    if (selectedText) {
      items.push({
        name: 'Copy',
        iconName: 'lucide:Copy',
        action: async () => {
          await navigator.clipboard.writeText(selectedText);
        }
      });
      items.push({ divider: true });
    }

    items.push(
      {
        name: 'Download PDF',
        iconName: 'lucide:Download',
        action: async () => {
          await this.downloadPdf();
        }
      },
      {
        name: 'Print PDF',
        iconName: 'lucide:Printer',
        action: async () => {
          await this.printPdf();
        }
      }
    );

    return items;
  }

  private get canZoomIn(): boolean {
    return this.viewportMode !== 'custom' || this.currentZoom < this.MANUAL_MAX_ZOOM;
  }

  private get canZoomOut(): boolean {
    return this.viewportMode !== 'custom' || this.currentZoom > this.MANUAL_MIN_ZOOM;
  }

  private readonly handleViewerWheel = (eventArg: WheelEvent): void => {
    const element = eventArg.currentTarget as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    const isAtTop = scrollTop === 0;
    const isAtBottom = Math.abs(scrollTop + clientHeight - scrollHeight) < 1;

    if (
      (eventArg.deltaY < 0 && !isAtTop)
      || (eventArg.deltaY > 0 && !isAtBottom)
    ) {
      eventArg.stopPropagation();
    } else if (
      (eventArg.deltaY < 0 && isAtTop)
      || (eventArg.deltaY > 0 && isAtBottom)
    ) {
      eventArg.preventDefault();
      eventArg.stopPropagation();
    }
  };

  private ensureViewerRefs() {
    if (!this.viewerMain) {
      this.viewerMain = this.shadowRoot?.querySelector('.viewer-main') as HTMLElement;
    }
    if (this.viewerMain && !this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.measureViewportDimensions();
        if (this.pdfDocument) {
          // Re-render all pages when viewport size changes
          this.reRenderAllPages();
          if (this.thumbnailsVisible) void this.renderThumbnails();
        }
      });
      this.resizeObserver.observe(this.viewerMain);
      this.measureViewportDimensions();
    }
    if (this.viewerMain && this.wheelEventTarget !== this.viewerMain) {
      this.wheelEventTarget?.removeEventListener('wheel', this.handleViewerWheel);
      this.wheelEventTarget = this.viewerMain;
      this.wheelEventTarget.addEventListener('wheel', this.handleViewerWheel, { passive: false });
    }
  }

  private measureViewportDimensions() {
    if (!this.viewerMain) {
      this.viewportDimensions = { width: 0, height: 0 };
      return;
    }

    const styles = getComputedStyle(this.viewerMain);
    const paddingX = parseFloat(styles.paddingLeft || '0') + parseFloat(styles.paddingRight || '0');
    const paddingY = parseFloat(styles.paddingTop || '0') + parseFloat(styles.paddingBottom || '0');
    const width = Math.max(this.viewerMain.clientWidth - paddingX, 0);
    const height = Math.max(this.viewerMain.clientHeight - paddingY, 0);
    this.viewportDimensions = { width, height };
  }

  private resolveInitialViewportMode() {
    if (typeof this.initialZoom === 'number') {
      this.viewportMode = 'custom';
      this.currentZoom = this.normalizeZoom(this.initialZoom, true);
    } else if (this.initialZoom === 'page-width') {
      this.viewportMode = 'page-width';
    } else if (this.initialZoom === 'page-fit' || this.initialZoom === 'auto') {
      this.viewportMode = 'page-fit';
    } else {
      this.viewportMode = 'auto';
    }

    if (this.viewportMode !== 'custom') {
      this.currentZoom = 1;
    }
  }

  private computeViewport(page: any) {
    this.measureViewportDimensions();
    const baseViewport = page.getViewport({ scale: 1 });
    let scale: number;

    switch (this.viewportMode) {
      case 'page-width': {
        const availableWidth = this.viewportDimensions.width || baseViewport.width;
        scale = availableWidth / baseViewport.width;
        break;
      }
      case 'page-fit':
      case 'auto': {
        const availableWidth = this.viewportDimensions.width || baseViewport.width;
        const availableHeight = this.viewportDimensions.height || baseViewport.height;
        const widthScale = availableWidth / baseViewport.width;
        const heightScale = availableHeight / baseViewport.height;
        scale = Math.min(widthScale, heightScale);
        break;
      }
      case 'custom':
      default: {
        scale = this.normalizeZoom(this.currentZoom || 1, false);
        break;
      }
    }

    if (!Number.isFinite(scale) || scale <= 0) {
      scale = 1;
    }

    const clampedScale = this.viewportMode === 'custom'
      ? this.normalizeZoom(scale, true)
      : this.normalizeZoom(scale, false);

    if (this.viewportMode !== 'custom') {
      this.currentZoom = clampedScale;
    }

    return page.getViewport({ scale: clampedScale });
  }

  private normalizeZoom(value: number, clampToManualRange: boolean) {
    const min = clampToManualRange ? this.MANUAL_MIN_ZOOM : this.ABSOLUTE_MIN_ZOOM;
    const max = clampToManualRange ? this.MANUAL_MAX_ZOOM : this.ABSOLUTE_MAX_ZOOM;
    return Math.min(Math.max(value, min), max);
  }

  private cleanupDocument(): Promise<void> {
    if (this.cleanupPromise) {
      return this.cleanupPromise;
    }
    const cleanupPromise = this.performDocumentCleanup();
    this.cleanupPromise = cleanupPromise;
    void cleanupPromise.finally(() => {
      if (this.cleanupPromise === cleanupPromise) {
        this.cleanupPromise = null;
      }
    }).catch(() => {});
    return cleanupPromise;
  }

  private async performDocumentCleanup(): Promise<void> {
    this.renderAdmissionOpen = false;
    this.renderGeneration += 1;
    this.rerenderQueueEpoch += 1;
    this.thumbnailRenderGeneration += 1;
    this.thumbnailRenderQueueEpoch += 1;
    this.thumbnailRenderQueued = false;
    if (this.rerenderRecoveryTimeout) {
      clearTimeout(this.rerenderRecoveryTimeout);
      this.rerenderRecoveryTimeout = undefined;
    }
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = undefined;
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.wheelEventTarget?.removeEventListener('wheel', this.handleViewerWheel);
    this.wheelEventTarget = null;
    if (this.scrollThrottleTimeout) {
      clearTimeout(this.scrollThrottleTimeout);
      this.scrollThrottleTimeout = undefined;
    }

    const abortController = this.renderAbortController;
    this.renderAbortController = null;
    abortController?.abort();
    const loadOperation = this.loadOperation;
    const pdfLoadPromise = this.pdfLoadPromise;
    const pageRenderTasks = [...this.pageRenderTasks.values()];
    const textLayerRenderTasks = [...this.textLayerRenderTasks.values()];
    const thumbnailRenderTasks = [...this.thumbnailRenderTasks];
    const pageRenderPipelines = [...this.pageRenderPipelines];
    const documentUsePipelines = [...this.documentUsePipelines];
    const rerenderPromise = this.rerenderPromise;
    this.rerenderQueued = false;
    const thumbnailRenderPromise = this.thumbnailRenderPromise;
    const currentRenderPromise = this.currentRenderPromise;
    for (const cleanup of this.textSelectionCleanupFunctions.values()) {
      cleanup();
    }
    this.textSelectionCleanupFunctions.clear();

    for (const task of pageRenderTasks) {
      try {
        task.cancel();
      } catch {
        // The tracked task promise still participates in settlement below.
      }
    }
    for (const task of textLayerRenderTasks) {
      try {
        task.textLayer.cancel();
      } catch {
        // The tracked task promise still participates in settlement below.
      }
    }
    for (const task of thumbnailRenderTasks) {
      try {
        task.cancel();
      } catch {
        // The tracked task promise still participates in settlement below.
      }
    }
    const renderCanvases = this.shadowRoot?.querySelectorAll<HTMLCanvasElement>(
      '.page-canvas, .thumbnail-canvas',
    );
    renderCanvases?.forEach((canvasArg) => {
      canvasArg.width = 0;
      canvasArg.height = 0;
      canvasArg.style.removeProperty('width');
      canvasArg.style.removeProperty('height');
    });

    const pdfDocument = this.pdfDocument;
    this.pdfDocument = null;
    const releasePromise = pdfDocument
      ? PdfManager.releaseDocument(pdfDocument).catch((error) => {
          console.error('Error destroying PDF document:', error);
        })
      : null;

    await settlePromisesWithin([
      ...(pdfLoadPromise ? [pdfLoadPromise] : []),
      ...(currentRenderPromise ? [currentRenderPromise] : []),
      ...documentUsePipelines,
      ...(rerenderPromise ? [rerenderPromise] : []),
      ...pageRenderPipelines,
      ...(thumbnailRenderPromise ? [thumbnailRenderPromise] : []),
      ...pageRenderTasks.map((task) => task.promise),
      ...textLayerRenderTasks.map((task) => task.promise),
      ...thumbnailRenderTasks.map((task) => task.promise),
      ...(releasePromise ? [releasePromise] : []),
    ], pipelineSettlementGraceMs);
    for (const cleanup of this.textSelectionCleanupFunctions.values()) {
      cleanup();
    }
    this.textSelectionCleanupFunctions.clear();

    if (this.pdfLoadPromise === pdfLoadPromise) {
      this.pdfLoadPromise = null;
    }
    if (this.loadOperation === loadOperation) {
      this.loadOperation = null;
    }
    this.currentRenderPromise = null;
    this.currentRenderTask = null;
    this.pageRenderTasks.clear();
    this.pageRenderPipelines.clear();
    this.documentUsePipelines.clear();
    if (this.rerenderPromise === rerenderPromise) {
      this.rerenderPromise = null;
    }
    this.textLayerRenderTasks.clear();
    for (const task of thumbnailRenderTasks) {
      this.thumbnailRenderTasks.delete(task);
    }
    if (this.thumbnailRenderPromise === thumbnailRenderPromise) {
      this.thumbnailRenderPromise = null;
    }

    this.loading = false;
    this.renderState = 'idle';
    this.pageRendering = false;
    this.pageNumPending = null;
    this.thumbnailObserver?.disconnect();
    this.thumbnailObserver = undefined;
    this.thumbnailObserverRoot = undefined;
    this.thumbnailData = [];
    this.pageData = [];
    this.documentId = '';
    this.totalPages = 1;
    this.currentPage = 1;
    this.pdfFileSize = 0;
    if (this.canvas && this.ctx) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    this.requestUpdate();
  }
}
